import React, { useEffect, useState } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Chip,
  Button,
} from '@mui/material';
import { useQuery } from 'react-query';
import apiClient from '@uniqcamp/shared/services/api.service';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import QRWithExpiry from './QRWithExpiry';
import NoticeCarousel from './NoticeCarousel';
import { useNetworkStatus } from '../hooks/useNetworkStatus';
import { exitFullscreen } from '../utils/fullscreen';

const TVDisplay = ({ schoolCode, gateId, otp, onBackToGates, onOtpInvalid }: { schoolCode: string; gateId: string; otp: string; onBackToGates?: () => void; onOtpInvalid?: () => void }) => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const online = useNetworkStatus();

  const otpParam = otp ? `?otp=${encodeURIComponent(otp)}` : '';
  const { data, isLoading, isError, error } = useQuery(
    ['tv-display', schoolCode, gateId],
    async () => {
      const res = await apiClient.get(`/tv-display/${schoolCode}/${gateId}${otpParam}`);
      return res.data;
    },
    {
      enabled: !!(schoolCode && gateId),
      refetchInterval: (data: { qrExpirySeconds?: number } | undefined) => {
        const sec = data?.qrExpirySeconds ?? 30;
        return Math.max(2000, (sec - 2) * 1000);
      },
      onError: (err: unknown) => {
        const ax = err as { response?: { status?: number; data?: { otpRequired?: boolean } } };
        if (ax.response?.status === 401 && ax.response?.data?.otpRequired && onOtpInvalid) {
          onOtpInvalid();
        }
      },
      retry: (failureCount, err) => {
        const ax = err as { response?: { status?: number; data?: { otpRequired?: boolean } } };
        if (ax.response?.status === 401 && ax.response?.data?.otpRequired) return false;
        return failureCount < 3;
      },
    }
  );

  useEffect(() => {
    const t = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  if (isLoading) {
    return (
      <Container maxWidth="md" sx={{ py: 4, textAlign: 'center' }}>
        <Typography>Loading…</Typography>
      </Container>
    );
  }

  if (isError) {
    return (
      <Container maxWidth="md" sx={{ py: 4, textAlign: 'center' }}>
        <Typography color="error">{(error as { message?: string })?.message || 'Failed to load'}</Typography>
      </Container>
    );
  }

  const d = data as {
    currentDispersalClass?: string;
    currentDispersalSection?: string;
    schoolName?: string;
    gateName?: string;
    gateEnabled?: boolean;
    useTvDisplay?: boolean;
    inDispersalTime?: boolean;
    qrPayload?: string;
    expiresAt?: string;
    displayNotice?: string;
    displayNotices?: { text: string; durationSeconds: number; imageDataUrl?: string }[] | string[];
    allowedClassesForGate?: { class: string; section: string; label: string }[];
    qrExpirySeconds?: number;
  };
  const useTvDisplay = d.useTvDisplay !== false;
  const gateEnabled = d.gateEnabled !== false;
  const inDispersalTime = d.inDispersalTime === true;
  const gateClasses = d.allowedClassesForGate || [];

  if (!useTvDisplay) {
    return (
      <Container maxWidth="md" sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
        <Paper
          sx={{
            p: 4,
            textAlign: 'center',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'action.hover',
          }}
        >
          <Typography variant="h5" color="text.secondary" sx={{ fontWeight: 600 }}>
            Display is disabled
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
            TV display is turned off in Principal Settings. Enable &quot;Use TV display&quot; to show dispersal at this gate.
          </Typography>
          {onBackToGates && (
            <Button startIcon={<ArrowBackIcon />} onClick={onBackToGates} sx={{ mt: 2 }}>
              Back to gates
            </Button>
          )}
        </Paper>
      </Container>
    );
  }

  const disabledBlock = (
    <Paper
      sx={{
        p: 4,
        textAlign: 'center',
        width: '100%',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'action.hover',
      }}
    >
      <Typography variant="h5" color="text.secondary" sx={{ fontWeight: 600 }}>
        This gate is disabled
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
        Dispersal is not available at this gate. Please use another gate or contact the school.
      </Typography>
    </Paper>
  );

  const qrBlock = (
    <Paper sx={{ p: 3, textAlign: 'center', width: '100%', boxSizing: 'border-box' }}>
      <Typography variant="h6" gutterBottom>Scan to request dispersal</Typography>
      {d.qrPayload && (
        <QRWithExpiry
          value={JSON.stringify(d.qrPayload)}
          size={280}
          expiresAt={d.expiresAt}
          totalSeconds={d.qrExpirySeconds ?? 30}
        />
      )}
    </Paper>
  );

  const rightBlock = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, flex: inDispersalTime ? '0 0 50%' : '1 1 100%', minWidth: 0 }}>
      {!inDispersalTime && (
        <Paper sx={{ p: 2, bgcolor: 'white', borderLeft: '4px solid', borderColor: 'warning.main' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'warning.dark', mb: 0.5 }}>
            Dispersal has not started yet
          </Typography>
          <Typography variant="body1" sx={{ fontSize: '1.05rem', color: 'warning.dark' }}>
            QR code will be available during dispersal time. Please come back later.
          </Typography>
        </Paper>
      )}
      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" color="text.secondary" gutterBottom sx={{ fontSize: '1.15rem', fontWeight: 600 }}>Classes at this gate for Dispersal </Typography>
        {gateClasses.length > 0 ? (
          <>
            {inDispersalTime && (
              <Typography variant="body1" color="text.secondary" sx={{ mb: 1, fontSize: '1.05rem' }}>
                Parents can request dispersal at this gate for:
              </Typography>
            )}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {gateClasses.map((ac, i) => (
                <Chip key={i} label={ac.label || `Class ${ac.class}${ac.section ? ' - ' + ac.section : ''}`} size="small" variant="outlined" sx={{ fontSize: '0.95rem' }} />
              ))}
            </Box>
          </>
        ) : (
          <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.05rem' }}>
            No class/section assigned to this gate. Configure in Settings → Gates.
          </Typography>
        )}
      </Paper>
      <Paper sx={{ p: 2, bgcolor: 'primary.50', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <Typography variant="subtitle1" color="text.secondary" gutterBottom sx={{ fontSize: '1.15rem', fontWeight: 600 }}>Notice</Typography>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <NoticeCarousel
          notices={
            d.displayNotices?.length
              ? d.displayNotices.map((n) =>
                  typeof n === 'string'
                    ? { text: n, durationSeconds: 5 }
                    : { text: n.text, durationSeconds: n.durationSeconds ?? 5, imageDataUrl: n.imageDataUrl }
                )
              : d.displayNotice
                ? [{ text: d.displayNotice, durationSeconds: 5 }]
                : []
          }
          />
        </Box>
      </Paper>
    </Box>
  );

  return (
    <Container maxWidth={false} sx={{ py: 2, height: '100vh', display: 'flex', flexDirection: 'column', width: '100%' }}>
      <Box sx={{ flexShrink: 0, mb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h4">{d.schoolName}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h5">{currentTime.toLocaleTimeString()}</Typography>
            <Chip
              label={online ? 'Online' : 'Offline'}
              color={online ? 'success' : 'error'}
              size="small"
            />
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          {onBackToGates && (
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => {
                exitFullscreen();
                onBackToGates();
              }}
              size="small"
              sx={{ flexShrink: 0 }}
            >
              Change gate
            </Button>
          )}
          <Typography variant="h6" color="text.secondary" gutterBottom sx={{ flex: 1, textAlign: 'center' }}>
            Gate: {d.gateName}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          flex: 1,
          minHeight: 0,
          '@media (orientation: landscape)': {
            flexDirection: 'row',
            alignItems: 'stretch',
            gap: 3,
            '& > *': { flex: '1 1 50%', minWidth: 0 },
          },
        }}
      >
        {gateEnabled ? (
          <>
            {inDispersalTime && qrBlock}
            {rightBlock}
          </>
        ) : (
          disabledBlock
        )}
      </Box>
    </Container>
  );
};

export default TVDisplay;
